<template>
	<div id="main-content" class="blog-page-wrapper">
		<!-- Breadcrumb Navigation -->
		<div class="nav-section constant-padding">
			<nav aria-label="Breadcrumb">
				<ol class="breadcrumb">
					<li class="breadcrumb-item">
						<NuxtLink to="/" :style="{ color: colors.primaryText }">Home</NuxtLink>
					</li>
					<ClientOnly>
						<v-icon :color="colors.secondaryText">mdi-chevron-right</v-icon>
					</ClientOnly>
					<li class="breadcrumb-item">
						<NuxtLink to="/resources/blogs" :style="{ color: colors.primaryText }">Resources</NuxtLink>
					</li>
					<ClientOnly>
						<v-icon :color="colors.secondaryText">mdi-chevron-right</v-icon>
					</ClientOnly>
					<li class="breadcrumb-item active" aria-current="page">
						{{ blogPost.title }}
					</li>
				</ol>
			</nav>
		</div>

		<!-- Blog Content -->
		<div class="blog-detail constant-padding" :style="blogDetailLayoutStyle">
			<article class="blog-container">
				<h1 ref="blogTitle" class="heading" :aria-label="`Blog Title: ${blogPost.title}`" tabindex="-1"
					:style="{ color: colors.primaryText }">{{ blogPost.title }}</h1>

				<div class="date-section">
					<ClientOnly>
						<v-icon :color="colors.secondaryText">mdi-calendar</v-icon>
					</ClientOnly>
					<span class="date">{{ formatDate(blogPost.published_at || blogPost.createdAt) }}</span>
				</div>

				<img v-if="blogPost.image" :src="blogPost.image" class="img-fluid"
					:class="{ 'img-contained': blogPost.imageLayout === 'contained' }"
					:style="bannerImageStyle"
					:alt="''" />

				<!-- Social Share -->
				<div class="social-share">
					<div class="social-icon">
						<a :href="`https://api.whatsapp.com/send?text=Check this article: ${encodeURIComponent(currentUrl)}`"
							target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp (opens in new tab)">
							<img src="/icons/WhatsAppFooter.svg" alt="Whatsapp Icon" aria-hidden="true" />
						</a>
					</div>
					<div class="social-icon">
						<a :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`"
							target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook (opens in new tab)">
							<img src="/social/facebook.svg" alt="Facebook Icon" aria-hidden="true" />
						</a>
					</div>
					<div class="social-icon">
						<button type="button" class="social-copy-btn" @click="copyUrl" @keydown.enter.prevent="copyUrl"
							@keydown.space.prevent="copyUrl" aria-label="Copy URL">
							<img src="/social/copy.svg" alt="Copy Icon" aria-hidden="true" />
							<span class="tooltiptext">Copied</span>
						</button>
					</div>
				</div>

				<!-- Blog Description -->
				<div v-html="blogPost.description" class="description constant-padding"></div>

				<section v-if="blogFaqItems.length" class="faq-section blog-faq-section">
					<h2 :style="{ color: colors.primaryText }">FAQs</h2>
					<div class="content">
						<CommonFaqAccordion
							v-for="(faq, index) in blogFaqItems"
							:key="faq._id || index"
							:faq="faq"
							:fetch-children="false"
						/>
					</div>
				</section>
			</article>

			<!-- CTA Banner - Desktop Only -->
			<div
				class="cta-banner"
				:class="{ 'desktop-view': !shouldForceShowCtaBanner }"
				:style="ctaBannerStyle"
			>
				<div class="cta-banner-content" :style="ctaBannerContentStyle">
					<div class="cta-illustration" :style="ctaIllustrationStyle">
						<img src="/images/blogCTA.svg" alt="Blog CTA" aria-hidden="true" class="img-fluid"
							role="presentation">
					</div>
					<h2 class="cta-heading" :style="{ color: colors.primaryText, ...ctaHeadingStyle }">Ready to start investing?</h2>
					<p class="cta-text" :style="{ color: colors.secondaryText, ...ctaTextStyle }">Open your Equirize account and
						begin
						your
						investment journey today.</p>
					<CommonButton class="cta-button sign_up_initiated" text="Get Started" :color="colors.buttonBg"
						width="100%" height="44px" :href="investEquirizeUrl" target="_blank"
						aria-label="Get Started with Equirize (opens in new tab)" />
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
	data() {
		return {
			currentUrl: '',
		}
	},
	mounted() {
		refreshNuxtData('data')
		this.currentUrl = window.location.href
		// console.log("url",this.currentUrl)
	},
})

</script>

<script lang="ts" setup>
import blogService from '~/service/blogService';
import { useTheme } from "~/composables/useTheme";
import { useUtmTracking } from "~/composables/useUtmTracking";
import { useRoute } from "vue-router";
import { computed, onBeforeUnmount, ref } from "vue";
import { CommonButton } from "#components";

const blogTitle = ref<HTMLElement | null>(null)
const { colors, themeName } = useTheme();
const { buildRedirectUrl } = useUtmTracking();
const route = useRoute();
const { investorUrl } = useInvestorPlatform();

const investEquirizeUrl = computed(() => {
	return buildRedirectUrl(investorUrl("/login"), route.path, "get-started-blog-sidebar");
});

const shouldForceShowCtaBanner = ref(false);
const shouldUseCompactLandscapeCta = ref(false);

const blogDetailLayoutStyle = computed(() => {
	if (!shouldForceShowCtaBanner.value) return {};
	if (shouldUseCompactLandscapeCta.value) {
		return {
			flexDirection: 'column',
			alignItems: 'stretch',
			gap: '32px'
		};
	}

	return {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: '50px'
	};
});

const ctaBannerStyle = computed(() => {
	if (!shouldForceShowCtaBanner.value) return {};
	if (shouldUseCompactLandscapeCta.value) {
		return {
			display: 'block',
			position: 'static',
			width: '100%',
			flexShrink: '0'
		};
	}

	return {
		display: 'block',
		position: 'sticky',
		top: '100px',
		width: '27%',
		flexShrink: '0'
	};
});

const ctaBannerContentStyle = computed(() => {
	if (!shouldForceShowCtaBanner.value) return {};
	const compactPadding = shouldUseCompactLandscapeCta.value ? '20px' : '32px 24px';

	return {
		background: 'linear-gradient(135deg, #E0F3FF 0%, #F0F9FF 100%)',
		border: '1px solid #B3E0FF',
		borderRadius: '12px',
		padding: compactPadding,
		boxShadow: '0px 4px 12px rgba(0, 39, 175, 0.1)'
	};
});

const ctaIllustrationStyle = computed(() => {
	if (!shouldForceShowCtaBanner.value) return {};

	return {
		display: 'flex',
		justifyContent: 'center',
		padding: '4% 4% 0 4%'
	};
});

const ctaHeadingStyle = computed(() => {
	if (!shouldForceShowCtaBanner.value) return {};

	return {
		fontSize: '20px',
		fontWeight: '700',
		margin: '0 0 12px 0',
		textAlign: 'center',
		lineHeight: '1.3'
	};
});

const ctaTextStyle = computed(() => {
	if (!shouldForceShowCtaBanner.value) return {};

	return {
		fontSize: '14px',
		lineHeight: '1.5',
		margin: '0 0 24px 0',
		textAlign: 'center'
	};
});

function isMobileDevice() {
	if (globalThis.window === undefined || globalThis.navigator === undefined) return false;

	const mobileUserAgent = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
	const coarsePointer = globalThis.matchMedia('(pointer: coarse)').matches;
	const shortestScreenSide = Math.min(window.screen.width, window.screen.height);

	return mobileUserAgent || (coarsePointer && shortestScreenSide <= 820);
}

function updateCtaBannerVisibility() {
	if (globalThis.window === undefined) return;

	const viewportWidth = globalThis.window.visualViewport?.width || globalThis.window.innerWidth;
	const viewportHeight = globalThis.window.visualViewport?.height || globalThis.window.innerHeight;
	const zoomedIntoTabletBreakpoint = globalThis.window.innerWidth <= 1024 && globalThis.window.devicePixelRatio >= 1.5;
	const landscapeViewport = globalThis.window.matchMedia('(orientation: landscape)').matches;
	const compactLandscapeViewport = landscapeViewport && globalThis.window.innerWidth <= 1024 && viewportHeight <= 500;
	const reflowViewport = viewportWidth <= 400;

	shouldUseCompactLandscapeCta.value = compactLandscapeViewport || reflowViewport;
	shouldForceShowCtaBanner.value = landscapeViewport || (!isMobileDevice() && zoomedIntoTabletBreakpoint);
}

function normalizeCssSize(value?: string) {
	const trimmed = value?.trim() || '';
	if (!trimmed) return '';
	if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
	return trimmed;
}

const bannerImageStyle = computed(() => {
	if (blogPost.imageLayout !== 'custom') return {};
	const width = normalizeCssSize(blogPost.imageCustomWidth);
	if (!width) return {};
	return {
		width,
		marginInline: 'auto',
		display: 'block'
	};
});
interface BlogPost {
	title: string;
	page_title?: string;
	titleUrl: string;
	image: string;
	createdAt: string;
	published_at?: string;
	metaDescription: string;
	tags: string[];
	description: string;
	updatedAt: string;
	imageLayout?: 'full' | 'contained' | 'custom';
	imageCustomWidth?: string;
	ldJson?: string;
	isDeleted?: boolean;
	category?: string;
}
let blogPost: BlogPost = {
	title: '',
	page_title: '',
	titleUrl: '',
	image: '',
	createdAt: '',
	published_at: '',
	metaDescription: '',
	tags: [],
	description: '',
	updatedAt: ''
}
let isLoading = true
let desc = ''
const blogPostSlug = route.params.titleUrl
// let currentUrl = ''
// onMounted( () =>
// {
// 	currentUrl = window.location.href
// });
// console.log("current url", currentUrl)
try {
	if (!blogPostSlug) {
		throw new Error('Blog post ID not found in route params')
	}
	const params = new URLSearchParams({ titleUrl: String(blogPostSlug) })
	const { data: post, error } = await useAsyncData<BlogPost>('data', () => blogService.getBlogDetail(params) as Promise<BlogPost>, {
		server: true
	})
	// console.log("value of post", post.value.isDeleted)
	if (post.value?.isDeleted == true || !post.value) {
		// console.log("if block post value")
		navigateTo('/404');
	}
	else {
		// console.log("else block console")
		if (post.value) {
			blogPost = post.value
			desc = blogPost.description
			isLoading = false;
		}
	}

} catch (error) {
	// console.log("catch block")
}

function formatDate(dateString) {
	const date = new Date(dateString);
	const options = { year: 'numeric', month: 'long', day: 'numeric' };
	return date.toLocaleDateString('en-US', options);
}
function copyUrl() {
	navigator.clipboard.writeText(window.location.href)
		.then(() => {

		})
		.catch((error) => {
		})
}

function extractLdJsonInput(value?: string) {
	const normalized = (value || '').replace(/\u00a0/g, ' ').trim();
	const scriptMatch = normalized.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/i);
	return (scriptMatch?.[1] || normalized).trim();
}

function isFaqPageSchema(schema: any) {
	const schemaType = schema?.['@type'];
	if (Array.isArray(schemaType)) {
		return schemaType.some((item: string) => String(item).toLowerCase() === 'faqpage');
	}
	return String(schemaType || '').toLowerCase() === 'faqpage';
}

function parseFaqPageSchema(value?: string) {
	const json = extractLdJsonInput(value);
	if (!json) return null;
	try {
		const schema = JSON.parse(json);
		return isFaqPageSchema(schema) ? schema : null;
	} catch (error) {
		return null;
	}
}

function getFaqItemsFromSchema(schema: any) {
	if (!schema || !Array.isArray(schema.mainEntity)) return [];
	return schema.mainEntity
		.map((item: any, index: number) => {
			const answer = Array.isArray(item?.acceptedAnswer)
				? item.acceptedAnswer[0]
				: item?.acceptedAnswer;
			return {
				_id: `blog-faq-${index}`,
				question: String(item?.name || '').trim(),
				answer: String(answer?.text || '').trim(),
				nested: []
			};
		})
		.filter((item: any) => item.question && item.answer);
}

function getJsonLdScript(value?: string) {
	const schema = parseFaqPageSchema(value);
	return schema ? JSON.stringify(schema) : null;
}

const jsonLdScript = getJsonLdScript(blogPost.ldJson);
const blogFaqItems = getFaqItemsFromSchema(parseFaqPageSchema(blogPost.ldJson));

const blogCanonical = `https://www.equirize.com/resources/blogs/${blogPost.titleUrl || ''}`
const blogSeoTitle = blogPost.page_title || blogPost.title

const articleSchema = JSON.stringify({
	"@context": "https://schema.org",
	"@type": "Article",
	"headline": blogPost.title,
	"description": blogPost.metaDescription,
	"image": blogPost.image,
	"datePublished": blogPost.published_at || blogPost.createdAt,
	"dateModified": blogPost.updatedAt || blogPost.published_at || blogPost.createdAt,
	"author": {
		"@type": "Organization",
		"name": "EquiRize",
		"url": "https://www.equirize.com/"
	},
	"publisher": {
		"@type": "Organization",
		"name": "EquiRize",
		"logo": {
			"@type": "ImageObject",
			"url": "https://www.equirize.com/icons/Logo-light.svg"
		}
	},
	"mainEntityOfPage": {
		"@type": "WebPage",
		"@id": blogCanonical
	},
	"keywords": (blogPost.tags || []).join(', ')
});

const headScripts: Array<{ type: string; innerHTML: string }> = [
	{ type: 'application/ld+json', innerHTML: articleSchema }
];
if (jsonLdScript) {
	headScripts.push({ type: 'application/ld+json', innerHTML: jsonLdScript });
}

useHead({
	title: blogSeoTitle,
	meta: [
		{ name: 'description', content: blogPost.metaDescription },
		{ name: 'keywords', content: blogPost.tags.join(', ') },
		{ property: 'og:type', content: 'article' },
		{ property: 'og:title', content: blogSeoTitle },
		{ property: 'og:description', content: blogPost.metaDescription },
		{ property: 'og:image', content: blogPost.image },
		{ property: 'og:url', content: blogCanonical },
		{ property: 'article:published_time', content: blogPost.published_at || blogPost.createdAt },
		{ property: 'article:modified_time', content: blogPost.updatedAt || blogPost.published_at || blogPost.createdAt },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: blogSeoTitle },
		{ name: 'twitter:description', content: blogPost.metaDescription },
		{ name: 'twitter:image', content: blogPost.image },
	],
	link: [
		{
			rel: "canonical",
			href: blogCanonical
		},
	],
	script: headScripts,
});

// useSchemaOrg([
// 	defineArticle({
// 		headline: blogPost.title,
// 		image: blogPost.image,
// 		datePublished: blogPost.createdAt,
//   		dateModified: blogPost.updatedAt
//   })
// ])

onMounted(async () => {
	updateCtaBannerVisibility();
	globalThis.window.addEventListener('resize', updateCtaBannerVisibility);
	globalThis.window.addEventListener('orientationchange', updateCtaBannerVisibility);
	globalThis.window.visualViewport?.addEventListener('resize', updateCtaBannerVisibility);

	// Wait for the next DOM update to ensure v-html has rendered
	await nextTick()

	// Programmatically move focus to the title
	if (blogTitle.value) {
		blogTitle.value.focus()
	}
})

onBeforeUnmount(() => {
	globalThis.window.removeEventListener('resize', updateCtaBannerVisibility);
	globalThis.window.removeEventListener('orientationchange', updateCtaBannerVisibility);
	globalThis.window.visualViewport?.removeEventListener('resize', updateCtaBannerVisibility);
})
</script>

<style scoped>
section {
	width: 100%;
	padding: 20px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.nav-section {
	width: 100%;
}

.breadcrumb {
	display: flex;
	align-items: center;
	padding: 1.4rem 0;
	list-style: none;
	font-size: 14px;
	font-weight: 500;
	margin: 0;
	color: #8c8c8c;
}

.breadcrumb-item.active {
	color: var(--secondary);
	font-weight: 600;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.blog-detail {
	display: flex;
	flex-direction: column;
	gap: 32px;
	width: 100%;
	position: relative;
}

.blog-container {
	width: 100%;
}

.cta-banner {
	display: none;
}

.heading {
	font-size: 16px;
	color: #232323;
	line-height: 1.45;
}

.heading:focus,
.heading:focus-visible {
	outline: none !important;
}

.date-section {
	display: flex;
	align-items: center;
	gap: 10px;
	color: #424D71;
	font-size: 12px;
	font-weight: 500;
	margin-bottom: 10px;
}

.img-fluid {
	width: 100%;
}

.img-contained {
	width: min(100%, 760px);
	display: block;
	margin-inline: auto;
}

.social-share {
	display: flex;
	align-items: center;
	margin-top:8px;
	gap: 12px;
}

.social-icon,
.social-icon a,
.social-copy-btn {
	width: 32px;
	height: 32px;
	flex: 0 0 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	line-height: 0;
}

.social-share img {
	width: 32px;
	height: 32px;
	display: block;
	object-fit: contain;
}

.social-icon:first-child img {
	width: 52px;
	height: 52px;
	max-width: none;
	transform: translate(-3px, 5px);
}

.social-copy-btn {
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
}

.social-icon .tooltiptext {
	visibility: hidden;
	width: 70px;
	padding: 1%;
	background-color: #000;
	color: #fff;
	text-align: center;
	font-size: 12px;
	border-radius: 6px;
	position: absolute;
}

.social-icon:active .tooltiptext {
	visibility: visible;
}

.description {
	padding-bottom: 5%;
	line-height: 1.5;
}

.description :deep(p) {
	margin: 1em 0;
	line-height: 1.5;
}

.description :deep(a) {
	color: #0027AF;
	text-decoration: underline;
	text-underline-offset: 2px;
}

.description :deep(a:hover),
.description :deep(a:focus-visible) {
	color: #001D82;
	outline: 2px solid #0027AF;
	outline-offset: 2px;
}

.description :deep([style*="color: #95a5a6"]),
.description :deep([style*="color:#95a5a6"]),
.description :deep([style*="color: rgb(149, 165, 166)"]) {
	color: #595959 !important;
}

.social-icon a:focus-visible,
.social-copy-btn:focus-visible {
	outline: 2px solid #0027AF;
	outline-offset: 2px;
	border-radius: 4px;
}

.description :deep(p:empty),
.description :deep(p:has(> br:only-child)) {
	min-height: 1.5em;
}

.description :deep(img) {
	max-width: 100%;
	height: auto;
}

.description :deep(iframe) {
	width: 100%;
	max-width: 100%;
	aspect-ratio: 16 / 9;
	height: auto;
}

.description :deep(table) {
	width: 100%;
	border-collapse: collapse;
	overflow-x: auto;
}

.description :deep(td),
.description :deep(th) {
	padding: 8px;
	border: 1px solid #D9DDE8;
}

.description :deep(em),
.description :deep(i),
.description :deep([style*="font-style: italic"]),
.description :deep([style*="font-style:italic"]) {
	font-style: italic !important;
}

.description :deep(.blog-content-button) {
	display: inline-block;
	padding: 10px 18px;
	border-radius: 6px;
	text-decoration: none;
	font-weight: 700;
}

.description :deep(.blog-author-note) {
	margin: 18px 0 20px;
	padding: 14px 16px;
	border-left: 4px solid #007AFF;
	border-radius: 6px;
	background: #F0F8FF;
	color: #232323;
	font-size: 14px;
	line-height: 1.6;
}

.description :deep(.blog-inline-cta) {
	margin: 20px 0;
}

.description :deep(.blog-inline-cta-link) {
	display: inline-block;
	max-width: 100%;
	box-sizing: border-box;
	padding: 10px 18px;
	border-radius: 6px;
	text-align: center;
	text-decoration: none;
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
}

.description :deep(.blog-video-embed) {
	width: 100%;
	max-width: 100%;
	margin: 20px auto;
}

.description :deep(.blog-video-embed iframe) {
	width: 100%;
	max-width: 100%;
	aspect-ratio: 16 / 9;
	height: auto;
	border: 0;
	display: block;
}

.blog-faq-section {
	padding: 24px 0 8px;
}

.blog-faq-section .content {
	margin-top: 20px;
	width: 96%;
}

@media (min-width: 1025px) {
	.blog-faq-section .content {
		width: 100%;
	}
}

/* Desktop Layout */
@media (min-width: 1025px) {
	.blog-detail {
		flex-direction: row;
		align-items: flex-start;
		gap: 50px;
	}

	.blog-container {
		flex: 1;
	}

	.cta-banner {
		display: block;
		position: sticky;
		top: 100px;
		width: 27%;
		flex-shrink: 0;
	}

	.cta-banner-content {
		background: linear-gradient(135deg, #E0F3FF 0%, #F0F9FF 100%);
		border: 1px solid #B3E0FF;
		border-radius: 12px;
		padding: 32px 24px;
		box-shadow: 0px 4px 12px rgba(0, 39, 175, 0.1);
	}

	.cta-illustration {
		display: flex;
		justify-content: center;
		padding: 4% 4% 0 4%;
	}

	.cta-heading {
		font-size: 20px;
		font-weight: 700;
		margin: 0 0 12px 0;
		text-align: center;
		line-height: 1.3;
	}

	.cta-text {
		font-size: 14px;
		line-height: 1.5;
		margin: 0 0 24px 0;
		text-align: center;
	}

	.cta-button {
		width: 100%;
	}
}

/* Responsive Heading */
@media (min-width: 767px) {
	.heading {
		font-size: 28px;
	}
}

.sign_up_initiated {}


.sign_up_initiated {}
</style>
